import axios from "axios";
import { useEffect, useState } from "react";
import { IncludeTestToggle } from "./TestAccounts";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { API, th, td, inp, StatPill } from "./shared";

const fmt = (n) => `${Number(n) < 0 ? "−" : ""}₹${Math.abs(Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const currentFy = () => {
  const d = new Date();
  return `FY${d.getMonth() >= 3 ? d.getFullYear() + 1 : d.getFullYear()}`;
};
const COLS = [
  ["orders", "Orders"], ["buyer_taxable_inr", "Buyer sales"], ["supplier_taxable_inr", "Supplier purchases"],
  ["goods_margin_inr", "Goods margin"], ["platform_fee_inr", "Platform fees"], ["earnings_inr", "Earnings (ex-GST)"],
  ["output_gst_inr", "Output GST"], ["input_gst_credit_inr", "ITC"], ["fee_gst_inr", "GST on fees"],
  ["note_adjustment_inr", "Note adj."], ["net_gst_payable_inr", "Net GST payable"], ["tds_inr", "TDS deducted"],
];
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });

export function MarginReport() {
  const [fy, setFy] = useState(currentFy());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [incTest, setIncTest] = useState(false);
  const fys = [0, 1, 2].map(i => `FY${Number(currentFy().slice(2)) - i}`);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/admin/reports/margin`, { params: { fy, include_test: incTest } }).then(r => setData(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || "Could not load margin report"); setData({ months: [], totals: {} }); });
  }, [fy, incTest]);

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/reports/margin.xlsx`, { params: { fy, include_test: incTest }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Margin_Report_${fy}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export the margin report"); } finally { setBusy(false); }
  };

  const t = data?.totals || {};
  return (
    <div data-testid="margin-report" style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:12,padding:"14px 16px",margin:"0 0 18px"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:12}}>
        <b style={{fontSize:14,color:"#0F172A"}}>Margin report</b>
        <span style={{fontSize:11.5,color:"#64748b"}}>Platform earnings, GST payable and TDS by invoice date</span>
        <span style={{flex:1}}/>
        <IncludeTestToggle value={incTest} onChange={setIncTest} count={data?.test_orders} testid="margin-include-test"/>
        <select data-testid="margin-fy" value={fy} onChange={e => setFy(e.target.value)} style={{...inp,width:120,marginTop:0}}>
          {fys.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <button data-testid="margin-export" className="primary-btn" disabled={busy || !data} onClick={exportXlsx} style={{padding:"8px 14px"}}>
          <Download size={14}/>{busy ? "Exporting…" : "Export Excel"}
        </button>
      </div>
      {!data ? <div data-testid="margin-loading" style={{fontSize:12,color:"#64748b"}}>Loading…</div> : (
        <>
          <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:12}}>
            <StatPill label="Platform earnings" value={fmt(t.earnings_inr)} tone="ok" testid="margin-total-earnings"/>
            <StatPill label="Net GST payable" value={fmt(t.net_gst_payable_inr)} tone="warn" testid="margin-total-gst"/>
            <StatPill label="TDS deducted" value={fmt(t.tds_inr)} testid="margin-total-tds"/>
            <StatPill label="Orders" value={t.orders || 0} testid="margin-total-orders"/>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",fontSize:11.5,borderCollapse:"collapse",background:"#fff"}}>
              <thead><tr style={{background:"#f3f4f6",textAlign:"right"}}>
                <th style={{...th,textAlign:"left"}}>Month</th>
                {COLS.map(([k, l]) => <th key={k} style={th}>{l}</th>)}
              </tr></thead>
              <tbody>
                {data.months.map(m => (
                  <tr key={m.month} data-testid={`margin-row-${m.month}`} style={{borderBottom:"1px solid #e5e7eb",textAlign:"right",color: m.orders ? "#111827" : "#9ca3af"}}>
                    <td style={{...td,textAlign:"left",fontWeight:600}}>{monthLabel(m.month)}</td>
                    {COLS.map(([k]) => <td key={k} style={{...td,fontWeight: k === "earnings_inr" || k === "net_gst_payable_inr" ? 600 : 400}}>{k === "orders" ? m[k] : fmt(m[k])}</td>)}
                  </tr>
                ))}
                <tr data-testid="margin-row-total" style={{background:"#EFF6FF",textAlign:"right",fontWeight:700}}>
                  <td style={{...td,textAlign:"left"}}>Total {fy}</td>
                  {COLS.map(([k]) => <td key={k} style={td}>{k === "orders" ? t[k] : fmt(t[k])}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
          <p style={{fontSize:11,color:"#64748b",margin:"8px 0 0"}}>Net GST payable = output GST − input tax credit + 18% GST on platform fees ± credit/debit notes. Delivery charges aren't included. The Excel file has a Monthly summary sheet and an Orders sheet listing every order.</p>
        </>
      )}
    </div>
  );
}
